/**
 * Composant Cart en Preact
 * Gère l'état du panier avec des hooks Preact
 */

import { render } from 'preact';
import { useState, useCallback, useEffect } from 'preact/hooks';

// Hook personnalisé pour gérer le panier
function useCart() {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem('cart');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Erreur lors du chargement du panier:', error);
      return [];
    }
  });

  const hideForm = useCallback(() => {
    const formRows = document.querySelectorAll('form#forminator-module-89 .forminator-row');
    formRows.forEach(row => {
      row.classList.add('hidden');
    });
  }, []);

  const saveToStorage = useCallback((newItems) => {

    try {
      localStorage.setItem('cart', JSON.stringify(newItems));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du panier:', error);
    }

    const cartProductIdsInput = document.querySelector('.cart-product-ids input');
    if (cartProductIdsInput) {
      cartProductIdsInput.value = JSON.stringify(newItems);
      cartProductIdsInput.dispatchEvent(new Event('change'));
    }
  }, []);

  const addItem = useCallback((product, quantity = 1) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      console.log('existingItem', existingItem);
      let newItems;

      if (existingItem) {
        newItems = prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newItems = [...prevItems, {
          ...product,
          quantity: quantity,
          addedAt: new Date().toISOString()
        }];
      }

      saveToStorage(newItems);
      return newItems;
    });
  }, [saveToStorage]);

  const removeItem = useCallback((productId) => {
    setItems(prevItems => {
      const newItems = prevItems.filter(item => item.id !== productId);
      saveToStorage(newItems);
      if (newItems.length === 0) {
        hideForm();
      }
      return newItems;
    });
  }, [saveToStorage]);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems(prevItems => {
      const newItems = prevItems.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      );
      saveToStorage(newItems);
      if (newItems.length === 0) {
        hideForm();
      }
      return newItems;
    });
  }, [removeItem, saveToStorage]);

  const clear = useCallback(() => {
    setItems([]);
    saveToStorage([]);
  }, [saveToStorage]);

  const getTotalItems = useCallback(() => {
    return items.reduce((total, item) => total + item.quantity, 0);
  }, [items]);

  const getTotalPrice = useCallback(() => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [items]);

  const isEmpty = useCallback(() => {
    return items.length === 0;
  }, [items]);

  return {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clear,
    getTotalItems,
    getTotalPrice,
    isEmpty,
    saveToStorage
  };
}

// Composant pour un article du panier
function CartItem({ item, onRemove, onUpdateQuantity }) {
  const handleIncrement = () => {
    onUpdateQuantity(item.id, item.quantity + 1);
  };

  const handleDecrement = () => {
    onUpdateQuantity(item.id, item.quantity - 1);
  };

  const handleQuantityChange = (e) => {
    const quantity = parseInt(e.target.value);
    if (quantity >= 0) {
      onUpdateQuantity(item.id, quantity);
    }
  };

  const handleRemove = () => {
    onRemove(item.id);
  };

  return (
    <div class="c-cart-item" data-cart-item={item.id}>
      <div class="c-cart-item__image">
        {item.mainPicture ? (
          <img src={`https://back.isc-recycle.com/images/${item.mainPicture}`} alt={item.name} class="c-cart-item__img" />
        ) : (
          <div class="c-cart-item__placeholder">
            <span class="c-cart-item__placeholder-icon">📦</span>
          </div>
        )}
      </div>

      <div class="c-cart-item__content">
        <article class="c-product">
          <p class="c-product__cat">{item.categories.map(category => category.name).join(', ')}</p>
          <span class="c-title fs-20 text-left">{item.name}</span>


          <div class="c-text">
            {item.shortDescription}
          </div>
          <p class="c-product__ref">Ref.: {item.ref}</p>
        </article>
      </div>

      <div class="c-product-cart__quantity-wrapper">
        <div class="c-product-cart__quantity">
          <button type="button" class="c-product-cart__quantity-btn c-product-cart__quantity-btn--decrement" aria-label="Diminuer la quantité" onClick={handleDecrement}>
            <span>−</span>
          </button>

          <input type="number" class="c-product-cart__quantity-input" data-cart-quantity={item.id} value={item.quantity} min="1" aria-label="Quantité" onChange={handleQuantityChange} />

          <button type="button" class="c-product-cart__quantity-btn c-product-cart__quantity-btn--increment" aria-label="Augmenter la quantité" onClick={handleIncrement}>
            <span>+</span>
          </button>
        </div>

        <button
          type="button"
          class="c-cart-item__remove-btn"
          onClick={handleRemove}
          title="Supprimer du panier"
          aria-label={`Supprimer ${item.name} du panier`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM17 6H7V19H17V6ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Composant principal du panier
function Cart({ showHeader = true, class: className = '' }) {
  const cart = useCart();
  const { items, addItem, removeItem, updateQuantity, isEmpty, saveToStorage } = cart;

  useEffect(() => {
    saveToStorage(items);
  }, [items]);

  const renderEmptyCart = () => (
    <div class="c-cart__empty">
      <div class="c-cart__empty-content">
        <p class="c-cart__empty-text">Aucun résultat trouvé</p>
        <br />
        <a href="/nos-produits" class="btn btn--primary">Voir les produits</a>
      </div>
    </div>
  );

  const renderCartItems = () => (
    <div class="c-cart__items">
      {items.map(item => (
        <CartItem
          key={item.id}
          item={item}
          onRemove={removeItem}
          onUpdateQuantity={updateQuantity}
        />
      ))}
    </div>
  );

  return (
    <div class={`c-cart ${className}`}>
      {showHeader && (
        <div class="c-cart__header">
          <p class="c-title fs-18 text-left">Mes produits :</p>
        </div>
      )}

      <div class="c-cart__container">
        {isEmpty() ? renderEmptyCart() : (
          <>
            {renderCartItems()}
          </>
        )}
      </div>
    </div>
  );
}

// Classe pour gérer l'intégration avec le système existant
export class CartManager {
  constructor() {
    this.cartInstance = null;
    this.container = null;
    this.form = document.querySelector('form#forminator-module-89');
    this.formRows = document.querySelectorAll('form#forminator-module-89 .forminator-row');
    this.init();
  }

  init() {
    this.container = document.querySelector('[data-cart-container]');
    if (this.container) {
      this.renderCart();
    }

    if (this.isEmpty()) {
      this.toggleForm();
    }

    this.bindEvents();
  }

  renderCart() {
    if (this.container) {
      render(<Cart />, this.container);
    }
  }

  bindEvents() {
    // Gestion des boutons d'ajout au panier
    document.addEventListener('click', (e) => {
      console.log('click', e.target);
      if (e.target.matches('[data-cart-add]')) {
        this.handleAddToCart(e.target);
      }
    });

    // Gestion des boutons de quantité (+ et -)
    document.addEventListener('click', (e) => {
      const inc = e.target.closest('[data-cart-increment]');
      const dec = e.target.closest('[data-cart-decrement]');
      if (inc) {
        this.handleQuantityIncrement(inc);
      } else if (dec) {
        this.handleQuantityDecrement(dec);
      }
    });

    // Gestion des changements d'input de quantité
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-quantity]')) {
        this.handleQuantityInputChange(e.target);
      }
    });

    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        this.emptyCart(); // on vide le panier
      });
    }

  }

  handleAddToCart(button) {
    const productId = button.dataset.productId;
    const productData = JSON.parse(button.dataset.productData);
    const quantity = parseInt(button.dataset.quantity) || 1;

    console.log('Ajout au panier:', { productId, productData, quantity });

    // Récupérer le panier depuis localStorage
    let cart = this.getCart();

    // Chercher si le produit existe déjà
    const existingItem = cart.find(item => item.id === parseInt(productId));
    if (existingItem) {
      // Mettre à jour la quantité
      existingItem.quantity += quantity;
    } else {
      // Ajouter un nouvel article
      const newItem = {
        ...productData,
        quantity: quantity,
        addedAt: new Date().toISOString()
      };
      cart.push(newItem);
    }

    // Sauvegarder dans localStorage
    this.saveCart(cart);

    // Animation de feedback
    this.showAddFeedback(button);
  }

  handleQuantityIncrement(button) {
    const productId = button.dataset.cartIncrement;
    const input = document.querySelector(`[data-cart-quantity="${productId}"]`);
    const addButton = document.querySelector(`[data-cart-add][data-product-id="${productId}"]`);

    if (input) {
      let quantity = (parseInt(input.value, 10) || 1) + 1;
      input.value = quantity;

      if (addButton) {
        addButton.dataset.quantity = quantity;
      }

      console.log(`Quantité incrémentée pour ${productId}:`, quantity);
    }
  }

  handleQuantityDecrement(button) {
    const productId = button.dataset.cartDecrement;
    const input = document.querySelector(`[data-cart-quantity="${productId}"]`);
    const addButton = document.querySelector(`[data-cart-add][data-product-id="${productId}"]`);

    if (input) {
      let quantity = parseInt(input.value) || 1;
      quantity = Math.max(1, quantity - 1); // Min 1
      input.value = quantity;

      // Mettre à jour le bouton d'ajout
      if (addButton) {
        addButton.dataset.quantity = quantity;
      }

      console.log(`Quantité décrémentée pour ${productId}:`, quantity);
    }
  }

  toggleForm() {
    if (this.formRows) {
      this.formRows.forEach(row => {
        console.log(row)
        row.classList.toggle('hidden');
      });
    } else {
      console.log('no form rows');
    }
  }

  handleQuantityInputChange(input) {
    const productId = input.dataset.cartQuantity;
    const addButton = document.querySelector(`[data-cart-add][data-product-id="${productId}"]`);

    let quantity = parseInt(input.value) || 1;
    input.value = quantity;

    // Mettre à jour le bouton d'ajout
    if (addButton) {
      addButton.dataset.quantity = quantity;
    }

    console.log(`Quantité modifiée manuellement pour ${productId}:`, quantity);
  }

  getCart() {
    try {
      const stored = localStorage.getItem('cart');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Erreur lors du chargement du panier:', error);
      return [];
    }
  }

  saveCart(cart) {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
      console.log('Panier sauvegardé:', cart);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du panier:', error);
    }
  }

  emptyCart() {
    this.saveCart([]);
    this.toggleForm();
  }

  showAddFeedback(button) {
    const originalText = button.textContent;
    button.textContent = 'Ajouté !';
    button.classList.add('cart-add-feedback');

    setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove('cart-add-feedback');
    }, 1500);
  }

  // Méthodes publiques pour l'intégration
  addItem(product, quantity = 1) {
    const cart = this.getCart();
    const existingItem = cart.find(item => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({
        ...product,
        quantity: quantity,
        addedAt: new Date().toISOString()
      });
    }

    this.saveCart(cart);
  }

  removeItem(productId) {
    const cart = this.getCart();
    const newCart = cart.filter(item => item.id !== productId);
    this.saveCart(newCart);

    if (this.isEmpty()) {
      this.toggleForm();
    }
  }

  getItems() {
    return this.getCart();
  }

  isEmpty() {
    return this.getCart().length === 0;
  }
}

// Export des composants
export { Cart, CartItem, useCart };

// Export par défaut
export default CartManager;
